import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BootstrapThemeService } from '@app/core/services/bootstrap-theme.service';
import { FdxUtilsService } from '@app/core/services/fdx-utils.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { SurfaceTierThemeService } from '@app/core/services/surface-tier-theme.service';
import { SurfaceUpgradeFeatureProp, SurfaceUpgradeFeatureService, SurfaceUpgradeFeatureType } from '@app/core/services/surface-upgrade-feature.service';
import { SurfaceAlertComponent } from '@app/modules/surface/surface-alert/surface-alert.component';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';

@Component({
    selector: 'fdx-surface-sku-limit-alert',
    templateUrl: './surface-sku-limit-alert.component.html',
    styleUrl: './surface-sku-limit-alert.component.scss',
    standalone: false
})
export class SurfaceSkuLimitAlertComponent extends SurfaceAlertComponent implements OnDestroy, OnInit, OnChanges {
    @Input() feature: SurfaceUpgradeFeatureProp;
    @Input() inlineLinks: boolean = false;
    @Input() canRememberDismissed: boolean = true;
    /**
     * Number of channels affected with this sku limit alert
     */
    @Input() channelCount?: number;
    @Input() plansUrl: string;
    @Input() productsUrl: string;
    @Output() readonly rememberDismissed?: EventEmitter<boolean> = new EventEmitter<boolean>();

    private readonly unsubscribe$: Subject<void> = new Subject<void>();
    protected featureType: SurfaceUpgradeFeatureType;
    protected readonly faArrowUpRightFromSquare: IconDefinition = faArrowUpRightFromSquare;

    // eslint-disable-next-line @typescript-eslint/typedef
    mainForm = new FormGroup({});

    isCollapsed: boolean = false;
    @ViewChild('collapse') collapse: NgbCollapse;

    get planButtonClass(): string {
        return `btn-${this.tier}`;
    }

    get textClass(): string {
        return `text-${this.surfaceTierTheme.getNextTierThemeType().bgLightTheme}`;
    }

    get formControlName(): string {
        return `${this.tier}-dont-show-sku-alert`
    }

    constructor(
        protected readonly bootstrapThemeService: BootstrapThemeService,
        private readonly fdxUtilsService: FdxUtilsService,
        protected readonly host: ElementRef<HTMLElement>,
        protected readonly localStorageService: LocalStorageService,
        protected readonly surfaceTierTheme: SurfaceTierThemeService,
        protected readonly surfaceUpgradeFeatureService: SurfaceUpgradeFeatureService
    ) {
        super(bootstrapThemeService, host, surfaceTierTheme);
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if ('feature' in changes) {
            const unformattedFeature: SurfaceUpgradeFeatureType = this.surfaceUpgradeFeatureService.getUpgradeFeatureType(this.feature);
            this.featureType = {
                ...unformattedFeature,
                descriptionHTML: this.formatDescriptionHTML(unformattedFeature)
            }
        }

        if (this.tier) {
            this.mainForm = new FormGroup({});
            this.mainForm.addControl(this.formControlName, new FormControl<boolean>(false));
        }
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next()
        this.unsubscribe$.complete()
    }

    /**
     * This method conditionally modifies the description to include a channel count.
     */
    formatDescriptionHTML(feature: SurfaceUpgradeFeatureType): string {
        const description = this.surfaceUpgradeFeatureService.getDescriptionHTML(feature, this.surfaceTierTheme.getCurrentTierThemeType());
        return (description.startsWith('#') && this.channelCount)
            ? `${this.channelCount.toLocaleString('en-US')}${description.slice(1)}`
            : description
    }

    onDismissed(): void {
        if (this.fdxUtilsService.hasReducedMotion) {
            this.handleDismissal();
            return;
        }

        this.collapse.toggle();
        setTimeout(() => {
            this.handleDismissal();
        }, 350);    // Match collapse animation duration
    }

    private handleDismissal(): void {
        this.rememberDismissed.emit(this.mainForm.value[this.formControlName]);
        this.host.nativeElement.remove();
    }
}
